import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { H1, Button, Li, Spinner } from 'shared';
import { usePostServicingV1InstrumentsInstrumentIdBankVerificationMutation, usePutServicingV1InstrumentsInstrumentIdBankVerificationExchangePublicTokenMutation, usePostServicingV1InstrumentsInstrumentIdBankAccountMutation, } from 'servicing-api';
import institutionIcon from 'utility-icons/institution.svg';
import { toastError } from 'utils';
import { setBankAccounts, setSelectedBankAccount } from '../actions';
import { useAuthorizationToken, useFlexFinalizeData } from '../hooks';
import { getPathWithToken } from '../utils';
function Autopay() {
    const dispatch = useDispatch();
    const [linkToken, setLinkToken] = useState(null);
    const [bankVerificationId, setBankVerificationId] = useState(null);
    const [linkTokenExpiresAt, setLinkTokenExpiresAt] = useState(null);
    const [shouldFetchToken, setShouldFetchToken] = useState(true);
    const history = useHistory();
    const authorizationToken = useAuthorizationToken();
    const { instrumentId } = useFlexFinalizeData(authorizationToken);
    const [postBankVerification, { isLoading }] = usePostServicingV1InstrumentsInstrumentIdBankVerificationMutation();
    const [exchangePublicToken, { isLoading: isExchangingToken }] = usePutServicingV1InstrumentsInstrumentIdBankVerificationExchangePublicTokenMutation();
    const [postBankAccount] = usePostServicingV1InstrumentsInstrumentIdBankAccountMutation();
    // Fetch a link token when needed
    useEffect(() => {
        if (instrumentId && authorizationToken && shouldFetchToken) {
            setShouldFetchToken(false);
            postBankVerification({ instrumentId: Number(instrumentId) }).then((result) => {
                if ('data' in result) {
                    const data = result.data;
                    setLinkToken(data.linkToken);
                    setBankVerificationId(data.bankVerificationId);
                    setLinkTokenExpiresAt(data.expiresAt);
                }
                else if ('error' in result) {
                    toastError('Unable to start bank verification. Please try again.');
                    setShouldFetchToken(true);
                }
            });
        }
    }, [instrumentId, authorizationToken, shouldFetchToken, postBankVerification]);
    const isTokenExpired = () => {
        if (!linkTokenExpiresAt)
            return true;
        const expiresAt = new Date(linkTokenExpiresAt);
        const now = new Date();
        // Consider expired 30 seconds early to avoid race conditions
        return now.getTime() >= expiresAt.getTime() - 30_000;
    };
    const refreshLinkToken = () => {
        setLinkToken(null);
        setBankVerificationId(null);
        setLinkTokenExpiresAt(null);
        setShouldFetchToken(true);
    };
    const handleOpen = () => {
        if (isTokenExpired()) {
            refreshLinkToken();
        }
        else {
            open();
        }
    };
    const { open, ready } = usePlaidLink({
        token: linkToken || '',
        onSuccess: (publicToken) => {
            // Exchange public token for access token
            if (instrumentId && bankVerificationId) {
                exchangePublicToken({
                    instrumentId: Number(instrumentId),
                    putServicingV1InstrumentsInstrumentIdBankVerificationExchangePublicToken: { publicToken, bankVerificationId },
                }).then((result) => {
                    if ('data' in result) {
                        const { plaidBankAccounts } = result.data;
                        if (plaidBankAccounts && plaidBankAccounts.length > 1) {
                            // Multiple bank accounts
                            dispatch(setBankAccounts(plaidBankAccounts));
                            history.push(getPathWithToken('/autopay-account-selection', authorizationToken));
                        }
                        else if (plaidBankAccounts && plaidBankAccounts.length === 1) {
                            // Single bank account
                            dispatch(setSelectedBankAccount(plaidBankAccounts[0]));
                            postBankAccount({
                                instrumentId: Number(instrumentId),
                                postServicingV1InstrumentsInstrumentIdBankAccount: {
                                    selectedBankAccountId: plaidBankAccounts[0].id,
                                },
                            }).then(() => {
                                history.push(getPathWithToken('/autopay-auth', authorizationToken));
                            });
                        }
                        else {
                            // No bank accounts - continue to autopay-auth as before
                            history.push(getPathWithToken('/autopay-auth', authorizationToken));
                        }
                    }
                    else if ('error' in result) {
                        toastError('Unable to connect your bank account. Please try again.');
                        refreshLinkToken();
                    }
                });
            }
        },
        onExit: () => {
            // User exited Plaid Link without completing — refresh the token
            // so the next attempt gets a fresh one
            refreshLinkToken();
        },
    });
    if (!instrumentId) {
        return (_jsx("div", { className: "pre-qualification-installment credit-portal pad-20", children: _jsx("div", { className: "text-center", children: _jsx(Spinner, {}) }) }));
    }
    return (_jsxs("div", { className: "autopay-container pre-qualification-installment credit-portal pad-20", children: [_jsx("div", { className: "approve-charge-icon justify-center mar-b-20", children: _jsx("img", { src: institutionIcon, alt: "agreement" }) }), _jsx(H1, { variant: "headline-large", className: "text-center", children: "Connect Bank Account" }), _jsx("div", { className: "text-center mar-t-20 mar-b-20", children: _jsxs("ul", { children: [_jsxs(Li, { variant: "title-large", listStyle: "disc", children: ["Connect your ", _jsx("strong", { children: "checking account" }), " now, which can be used to make payments."] }), _jsx(Li, { variant: "title-large", listStyle: "disc", className: "mar-t-20", children: "Your banking information may be used for future payments." }), _jsx(Li, { variant: "title-large", listStyle: "disc", className: "mar-t-20", children: "You will not be debited and no payment is due at this time." })] }) }), _jsx("div", { className: "flex-center mar-t-20", children: _jsx(Button, { type: "submit", className: "button-primary button-warn button-warn-alp-flex w90", onClick: handleOpen, disabled: !ready || isLoading || isExchangingToken || !linkToken, children: isLoading
                        ? 'Loading...'
                        : isExchangingToken
                            ? 'Connecting...'
                            : 'Connect Bank Account' }) })] }));
}
export default Autopay;
